/**
 * Annuaire.jsx — Annuaire des auto-écoles de France (données data.gouv.fr).
 *
 * Source : Sécurité Routière · Phase 33.
 * Route publique : /annuaire
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Award, Globe, Phone, ChevronLeft, ChevronRight, GraduationCap, Sparkles, Users, Map as MapIcon, List, ShieldCheck, Info, Navigation, Loader2, Crown } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/leaflet.markercluster.js';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

// Fix Leaflet default icon path (bundlers strip default markers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const roseIcon = new L.DivIcon({
  className: 'c2r-marker',
  html: '<div style="width:22px;height:22px;background:#e11d48;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.5);"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const API = process.env.REACT_APP_BACKEND_URL;

const PERMIS_OPTIONS = [
  { value: 'B', label: 'Permis B', desc: 'Voiture · 18 ans' },
  { value: 'AM', label: 'Permis AM', desc: 'Voiturette · 14 ans' },
  { value: 'B96', label: 'Permis B96', desc: 'Remorque > 750 kg' },
  { value: 'BE', label: 'Permis BE', desc: 'Grosse remorque' },
];

function SchoolCard({ school }) {
  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${school.nom} ${school.adresse} ${school.ville}`,
  )}`;
  return (
    <div
      className={`rounded-2xl bg-[#141416] border transition p-4 sm:p-5 flex flex-col gap-3 ${school.featured_rank != null ? 'border-amber-400/50 ring-1 ring-amber-400/25 bg-[#1a1712]' : 'border-white/10 hover:border-rose-500/30 hover:bg-[#181819]'}`}
      data-testid={`school-card-${school.id}`}
    >
      {school.featured_rank != null && (
        <span className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 text-[9px] font-black uppercase tracking-widest" data-testid={`school-featured-badge-${school.id}`}>
          ★ {school.featured_partner || 'Mise en avant'}
        </span>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3
            className="text-white font-black text-[15px] sm:text-[16px] leading-tight line-clamp-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {school.nom}
          </h3>
          <p className="text-white/50 text-[11.5px] mt-1 flex items-start gap-1.5">
            <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              {school.adresse}
              <br />
              {school.code_postal} {school.ville}
              {typeof school.distance_km === 'number' && (
                <span className="ml-2 inline-flex items-center gap-0.5 text-emerald-300 font-mono text-[10.5px]">
                  <Navigation className="w-2.5 h-2.5" /> {school.distance_km} km
                </span>
              )}
            </span>
          </p>
        </div>
        {school.label_qualite && (
          <span className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] font-black uppercase tracking-widest" title="Label Qualité des Formations">
            <Award className="w-2.5 h-2.5" /> Qualité
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {school.permis.map((p) => (
          <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10px] font-black uppercase tracking-widest">
            {p}
          </span>
        ))}
        {school.permis_a_prix_reduit && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-black uppercase tracking-widest">
            Permis 1€
          </span>
        )}
        {school.ecole_associative && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-[10px] font-black uppercase tracking-widest">
            <Users className="w-2.5 h-2.5" /> Assoc.
          </span>
        )}
        {typeof school.taux_reussite_moto === 'number' && (
          <span className="ml-auto text-white/60 text-[11px] font-mono">
            🏁 {Math.round(school.taux_reussite_moto * 100)}% réussite
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1 mt-auto">
        <a
          href={gmapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-white/15 bg-white/5 text-white hover:bg-white/10 text-[11px] font-semibold transition"
          data-testid={`school-map-${school.id}`}
        >
          <MapPin className="w-3.5 h-3.5" /> Voir sur la carte
        </a>
        {school.site_internet && (
          <a
            href={school.site_internet.startsWith('http') ? school.site_internet : `https://${school.site_internet}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-black uppercase tracking-widest transition"
            data-testid={`school-website-${school.id}`}
          >
            <Globe className="w-3.5 h-3.5" /> Site
          </a>
        )}
        {school.ville_slug && school.departement && (
          <Link
            to={`/auto-ecoles/${school.departement}/${school.ville_slug}/${school.id}/gerer`}
            className="ml-auto inline-flex items-center gap-1 h-9 px-2.5 rounded-xl border border-white/10 bg-white/[0.03] text-white/60 hover:text-white hover:bg-white/10 text-[10.5px] transition"
            data-testid={`school-gerer-${school.id}`}
          >
            <Info className="w-3 h-3" /> Modifier / Retirer
          </Link>
        )}
      </div>
    </div>
  );
}

function MapView({ items }) {
  // Init impératif Leaflet — évite l'erreur "Map container is already initialized"
  // liée à React StrictMode qui double-monte les composants.
  const containerRef = React.useRef(null);
  const mapRef = React.useRef(null);

  React.useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) {
      try { mapRef.current.off(); mapRef.current.remove(); } catch (_) {}
      mapRef.current = null;
    }
    const map = L.map(containerRef.current, {
      center: [46.6, 2.4],
      zoom: 6,
      scrollWheelZoom: true,
      preferCanvas: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    // Cluster group persistant
    map._clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      showCoverageOnHover: false,
      maxClusterRadius: 55,
      spiderfyOnMaxZoom: true,
    });
    map.addLayer(map._clusterGroup);
    return () => {
      if (mapRef.current) {
        try { mapRef.current.off(); mapRef.current.remove(); } catch (_) {}
        mapRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!mapRef.current || !mapRef.current._clusterGroup) return;
    mapRef.current._clusterGroup.clearLayers();
    const markers = [];
    items.forEach((s) => {
      if (!s.lat || !s.lng) return;
      const popupHtml = `
        <div style="min-width:200px">
          <div style="font-weight:900;font-size:13px;margin-bottom:4px">${s.nom}</div>
          <div style="font-size:11px;color:#666">${s.adresse}<br/>${s.code_postal} ${s.ville}</div>
          <div style="margin-top:6px;font-size:11px">Permis : <strong>${s.permis.join(' · ')}</strong></div>
          ${s.site_internet ? `<a href="${s.site_internet.startsWith('http') ? s.site_internet : 'https://' + s.site_internet}" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-top:6px;color:#e11d48;font-weight:700;font-size:11px">Site internet →</a>` : ''}
          ${s.ville_slug ? `<a href="/auto-ecoles/${s.departement}/${s.ville_slug}" style="display:inline-block;margin-top:6px;margin-left:8px;color:#3b82f6;font-weight:700;font-size:11px">Fiche ville →</a>` : ''}
        </div>`;
      const m = L.marker([s.lat, s.lng], { icon: roseIcon }).bindPopup(popupHtml);
      markers.push(m);
    });
    if (markers.length) {
      mapRef.current._clusterGroup.addLayers(markers);
    }
    // Zoom auto sur les markers si plus de 1
    if (items.length > 1) {
      const bounds = L.latLngBounds(items.filter((s) => s.lat && s.lng).map((s) => [s.lat, s.lng]));
      if (bounds.isValid()) mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
    }
  }, [items]);

  return (
    <div>
      <div ref={containerRef} className="rounded-2xl overflow-hidden border border-white/10" style={{ height: '65vh', minHeight: 480, background: '#0d0d0e' }} data-testid="annuaire-map" />
      <div className="text-white/40 text-[11px] mt-2 text-center">
        Carte : {items.length} école{items.length > 1 ? 's' : ''} affichée{items.length > 1 ? 's' : ''} (max 500 · affine les filtres pour ta région)
      </div>
    </div>
  );
}

export default function Annuaire() {
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('');
  const [permis, setPermis] = useState([]);
  const [page, setPage] = useState(1);
  const [view, setView] = useState('list'); // 'list' | 'map'
  const [data, setData] = useState({ items: [], total: 0, pages: 0 });
  const [mapItems, setMapItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [departements, setDepartements] = useState([]);
  const [stats, setStats] = useState({ count: 0 });

  // Géoloc « Autour de moi »
  const [nearbyItems, setNearbyItems] = useState(null); // null = pas activé
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [origin, setOrigin] = useState(null); // {lat, lng}

  const findNearby = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setGeoError("La géolocalisation n'est pas disponible sur votre appareil.");
      return;
    }
    setLocating(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setOrigin({ lat: latitude, lng: longitude });
        try {
          const params = new URLSearchParams({ lat: String(latitude), lng: String(longitude), radius: '30000', limit: '40' });
          if (permis.length) params.set('permis', permis.join(','));
          const res = await fetch(`${API}/api/annuaire/nearby?${params.toString()}`);
          const json = await res.json();
          setNearbyItems(json.items || []);
        } catch (e) {
          setGeoError('Impossible de récupérer les auto-écoles à proximité.');
        }
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setGeoError(
          err.code === err.PERMISSION_DENIED
            ? 'Autorisation refusée. Activez la géolocalisation dans les réglages de votre navigateur.'
            : "Impossible d'obtenir votre position."
        );
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
    );
  }, [permis]);

  const clearNearby = () => { setNearbyItems(null); setOrigin(null); setGeoError(null); };

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '24' });
      if (q.trim()) params.set('q', q.trim());
      if (dept) params.set('dept', dept);
      if (permis.length) params.set('permis', permis.join(','));
      const res = await fetch(`${API}/api/annuaire?${params.toString()}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [q, dept, permis, page]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  // Charge un lot plus large pour la carte (jusqu'à 500 markers max pour la perf)
  useEffect(() => {
    if (view !== 'map') return;
    const params = new URLSearchParams({ page: '1', limit: '500' });
    if (q.trim()) params.set('q', q.trim());
    if (dept) params.set('dept', dept);
    if (permis.length) params.set('permis', permis.join(','));
    fetch(`${API}/api/annuaire?${params.toString()}`)
      .then((r) => r.json())
      .then((json) => setMapItems((json.items || []).filter((s) => s.lat && s.lng)))
      .catch(() => {});
  }, [view, q, dept, permis]);

  useEffect(() => {
    fetch(`${API}/api/annuaire/departements`).then((r) => r.json()).then(setDepartements).catch(() => {});
    fetch(`${API}/api/annuaire/stats`).then((r) => r.json()).then(setStats).catch(() => {});
  }, []);

  const togglePermis = (p) => {
    setPage(1);
    setPermis((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 lg:pb-12">
      {/* HERO */}
      <header className="border-b border-white/10 bg-gradient-to-b from-rose-950/40 via-transparent to-transparent">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10px] font-black uppercase tracking-widest mb-3">
            <GraduationCap className="w-3 h-3" /> Annuaire officiel
          </div>
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Trouve ton auto-école
          </h1>
          <p className="text-white/70 text-sm sm:text-base mt-2 max-w-2xl leading-relaxed">
            {stats.count > 0 && (
              <>
                <strong className="text-white">{stats.count.toLocaleString('fr-FR')} auto-écoles</strong> agréées en France · Base
                {' '}<a href="https://www.data.gouv.fr/datasets/liste-des-auto-ecoles-et-taux-de-reussite-au-permis-de-conduire/" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-rose-300">Sécurité Routière</a> — permis B, AM, B96, BE.
              </>
            )}
          </p>
        </div>
      </header>

      {/* RECHERCHE */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); clearNearby(); }}
              placeholder="Rechercher par ville, code postal ou nom d'école…"
              className="w-full h-12 pl-11 pr-4 rounded-2xl bg-[#141416] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-rose-500/50 focus:ring-2 focus:ring-rose-500/20 transition"
              data-testid="annuaire-search"
            />
          </div>
          <button
            type="button"
            onClick={findNearby}
            disabled={locating}
            className={`inline-flex items-center justify-center gap-1.5 h-12 px-4 rounded-2xl border font-black uppercase tracking-widest text-[12px] transition ${
              nearbyItems
                ? 'bg-emerald-600 border-emerald-500 text-white shadow-lg shadow-emerald-900/40'
                : 'bg-white/[0.03] border-white/15 text-white hover:bg-white/[0.06] hover:border-emerald-500/40'
            } disabled:opacity-60`}
            data-testid="annuaire-nearby-btn"
            aria-label="Trouver les auto-écoles à proximité"
          >
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
            <span>{nearbyItems ? 'Autour de moi actif' : 'Autour de moi'}</span>
          </button>
        </div>

        {geoError && (
          <div className="rounded-xl bg-rose-950/30 border border-rose-500/30 text-rose-200 text-[12px] px-3 py-2 flex items-start gap-2" data-testid="annuaire-geo-error">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              {geoError}
              <button type="button" onClick={clearNearby} className="ml-2 underline hover:text-white">Fermer</button>
            </div>
          </div>
        )}

        {nearbyItems && !geoError && (
          <div className="rounded-xl bg-emerald-950/30 border border-emerald-500/30 text-emerald-200 text-[12px] px-3 py-2 flex items-center justify-between gap-2" data-testid="annuaire-nearby-active">
            <span className="inline-flex items-center gap-1.5">
              <Navigation className="w-3.5 h-3.5" />
              <strong>{nearbyItems.length}</strong>&nbsp;écoles trouvées dans un rayon de 30 km autour de vous
            </span>
            <button type="button" onClick={clearNearby} className="underline hover:text-white text-[11px]" data-testid="annuaire-nearby-clear">
              Retour à la recherche
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={dept}
            onChange={(e) => { setDept(e.target.value); setPage(1); }}
            className="h-10 px-3 rounded-xl bg-[#141416] border border-white/10 text-white text-[13px] focus:outline-none focus:border-rose-500/50"
            data-testid="annuaire-dept"
          >
            <option value="">Tous les départements</option>
            {departements.map((d) => (
              <option key={d.code} value={d.code}>{d.code} ({d.count})</option>
            ))}
          </select>
          {PERMIS_OPTIONS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => togglePermis(p.value)}
              data-testid={`annuaire-permis-${p.value}`}
              className={`h-10 px-3 rounded-xl border text-[11.5px] font-black uppercase tracking-widest transition ${
                permis.includes(p.value)
                  ? 'bg-rose-500 border-rose-400 text-white shadow-lg shadow-rose-900/40'
                  : 'bg-white/[0.03] border-white/10 text-white/70 hover:bg-white/[0.06]'
              }`}
              title={p.desc}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="text-white/50 text-[12px] font-mono">
            {loading ? 'Recherche…' : `${data.total.toLocaleString('fr-FR')} école${data.total > 1 ? 's' : ''} trouvée${data.total > 1 ? 's' : ''}`}
          </div>
          <div className="inline-flex rounded-xl bg-white/[0.03] border border-white/10 p-0.5">
            <button
              type="button"
              onClick={() => setView('list')}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition ${
                view === 'list' ? 'bg-rose-500 text-white shadow-md' : 'text-white/60 hover:text-white'
              }`}
              data-testid="view-list"
            >
              <List className="w-3.5 h-3.5" /> Liste
            </button>
            <button
              type="button"
              onClick={() => setView('map')}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition ${
                view === 'map' ? 'bg-rose-500 text-white shadow-md' : 'text-white/60 hover:text-white'
              }`}
              data-testid="view-map"
            >
              <MapIcon className="w-3.5 h-3.5" /> Carte
            </button>
          </div>
        </div>
      </div>

      {/* RÉSULTATS */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        {view === 'map' ? (
          <MapView items={nearbyItems && nearbyItems.length ? nearbyItems : mapItems} />
        ) : nearbyItems ? (
          nearbyItems.length === 0 ? (
            <div className="rounded-2xl bg-[#141416] border border-white/10 p-8 text-center text-white/50 text-sm">
              Aucune auto-école trouvée dans un rayon de 30 km autour de votre position.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {nearbyItems.map((s) => (
                <SchoolCard key={s.id} school={s} />
              ))}
            </div>
          )
        ) : data.items.length === 0 && !loading ? (
          <div className="rounded-2xl bg-[#141416] border border-white/10 p-8 text-center text-white/50 text-sm">
            Aucune école ne correspond à ta recherche.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {data.items.map((s) => (
              <SchoolCard key={s.id} school={s} />
            ))}
          </div>
        )}
      </div>

      {/* PAGINATION (masquée en mode 'autour de moi') */}
      {!nearbyItems && data.pages > 1 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-white disabled:opacity-30 hover:bg-white/10 text-[12px] font-semibold transition"
            data-testid="annuaire-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Précédent
          </button>
          <div className="text-white/60 text-[12px] font-mono px-3">
            Page {page} / {data.pages}
          </div>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
            disabled={page >= data.pages}
            className="inline-flex items-center gap-1 h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-white disabled:opacity-30 hover:bg-white/10 text-[12px] font-semibold transition"
            data-testid="annuaire-next"
          >
            Suivant <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* FOOTER SOURCE */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 text-[11px] text-white/40 space-y-1">
        <p>
          <ShieldCheck className="inline w-3 h-3 mr-1 text-emerald-400" />
          <strong className="text-white/60">Source</strong> : Sécurité Routière · <a href="https://www.data.gouv.fr/datasets/liste-des-auto-ecoles-et-taux-de-reussite-au-permis-de-conduire/" target="_blank" rel="noopener noreferrer" className="underline hover:text-rose-300">data.gouv.fr</a> · Certaines écoles peuvent ne plus être en activité — vérifiez sur place.
        </p>
        <p>
          <Link to="/auto-ecoles" className="underline hover:text-rose-300">Annuaire par département</Link> · <Link to="/mentions-annuaire" className="underline hover:text-rose-300">Mentions légales &amp; RGPD</Link>
        </p>
      </div>
    </div>
  );
}
